import React from "react";
import { ArrowUpRight, CalendarDays, FileText, PenSquare, Rows3 } from "lucide-react";
import Shimmer from "../../shimmer/Shimmer";
import useWeeklyWorkQueue from "../../../hooks/useWeeklyWorkQueue";
import useWeeklyPlansForBrands from "../../../hooks/useWeeklyPlansForBrands";
import { BrandDetails } from "../../../types/add-brand-types";
import { ContentLog } from "../../../types/dashboard";

interface WeeklyWorkQueueProps {
  brandList: BrandDetails[] | null | undefined;
  contentLogs: ContentLog[] | null | undefined;
  isLoading: boolean;
  onOpenWeeklyPlan: (brand: BrandDetails) => void;
  onOpenCreator: (brand: BrandDetails) => void;
  onOpenHistory: (brand: BrandDetails) => void;
  onOpenCalendar: (brand: BrandDetails) => void;
}

const WeeklyWorkQueue: React.FC<WeeklyWorkQueueProps> = ({
  brandList,
  contentLogs,
  isLoading,
  onOpenWeeklyPlan,
  onOpenCreator,
  onOpenHistory,
  onOpenCalendar,
}) => {
  const { plansByBrandId, loading: loadingPlans } =
    useWeeklyPlansForBrands(brandList);
  const rows = useWeeklyWorkQueue(brandList, contentLogs, plansByBrandId);

  if (isLoading || loadingPlans) {
    return (
      <div className="ds-card ds-table-card">
        <div className="ds-weekly-queue-loading">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="ds-weekly-queue-loading-row">
              <Shimmer width="16%" height={18} />
              <Shimmer width="20%" height={18} />
              <Shimmer width="38%" height={18} />
              <Shimmer width="12%" height={18} />
              <Shimmer width="14%" height={18} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!brandList || brandList.length === 0) {
    return (
      <div className="ds-card ds-weekly-queue-empty">
        <Rows3 size={28} className="ds-empty-icon" />
        <p className="ds-empty-title">No weekly work queue yet</p>
        <p className="ds-empty-desc">
          Add a brand to generate weekly plans and track current work.
        </p>
      </div>
    );
  }

  return (
    <div className="ds-card ds-table-card">
      <div className="ds-table-wrap">
        <table className="ds-table ds-weekly-queue-table">
          <thead>
            <tr>
              <th>Brand</th>
              <th>Context</th>
              <th>Queue</th>
              <th>This week</th>
              <th>Open</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const brand = brandList.find((item) => item.id === row.brandId);
              if (!brand) {
                return null;
              }

              return (
                <tr key={row.brandId}>
                  <td>
                    <div className="ds-weekly-queue-brand-cell">
                      <div className="ds-cell-brand">{row.brandName}</div>
                      <div className="ds-cell-muted">{row.queueSourceLabel}</div>
                    </div>
                  </td>
                  <td>
                    <span
                      className={`ds-weekly-queue-context-badge ds-weekly-queue-context-badge--${row.contextReadiness.tone}`}
                    >
                      {row.contextReadiness.label}
                    </span>
                  </td>
                  <td>
                    <ol className="ds-weekly-queue-items">
                      {row.derivedItems.map((item, index) => (
                        <li key={`${row.brandId}-${index}`}>{item}</li>
                      ))}
                    </ol>
                  </td>
                  <td>
                    <div className="ds-weekly-queue-counts">
                      <span className="ds-weekly-queue-count">
                        <strong>{row.openThisWeek}</strong>
                        <span>open</span>
                      </span>
                      <span className="ds-weekly-queue-count">
                        <strong>{row.scheduledThisWeek}</strong>
                        <span>scheduled</span>
                      </span>
                      <span className="ds-weekly-queue-count">
                        <strong>{row.publishedThisWeek}</strong>
                        <span>published</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="ds-weekly-queue-actions">
                      <button
                        type="button"
                        className="ds-weekly-queue-action"
                        onClick={() => onOpenWeeklyPlan(brand)}
                        title="Open weekly plan"
                        aria-label={`Open ${row.brandName} weekly plan`}
                      >
                        <ArrowUpRight size={15} />
                      </button>
                      <button
                        type="button"
                        className="ds-weekly-queue-action"
                        onClick={() => onOpenCreator(brand)}
                        title="Open creator studio"
                        aria-label={`Open ${row.brandName} creator studio`}
                      >
                        <PenSquare size={15} />
                      </button>
                      <button
                        type="button"
                        className="ds-weekly-queue-action"
                        onClick={() => onOpenHistory(brand)}
                        title="Open history"
                        aria-label={`Open ${row.brandName} history`}
                      >
                        <FileText size={15} />
                      </button>
                      <button
                        type="button"
                        className="ds-weekly-queue-action"
                        onClick={() => onOpenCalendar(brand)}
                        title="Open calendar"
                        aria-label={`Open ${row.brandName} calendar`}
                      >
                        <CalendarDays size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyWorkQueue;
